/**
 * Environment/config access: build-time (process.env) first, then runtime config (config.json).
 * Same order as runtimeConfig: .env takes priority when set; otherwise config.json is used.
 *
 * Runtime vars (API URI, logging enabled, etc.) should live in config.json only — not in .env.
 */

import { getConfigValue as getConfigValueFromRuntime, getRuntimeConfig } from './runtimeConfig';

/**
 * Gets a config value: process.env (build-time) first, then runtime config, then fallback.
 */
export function getConfigValue(key: string, fallback: string = ''): string {
  const value = getConfigValueFromRuntime(key, fallback);
  return value ?? fallback;
}

/**
 * Gets an environment variable value (same as getConfigValue, for compatibility).
 */
export function getEnv(key: string, defaultValue?: string): string | undefined {
  return getConfigValueFromRuntime(key, defaultValue);
}

/**
 * Returns a config value as a boolean.
 * Treats 'true' and '1' (case-insensitive) as true, everything else as false.
 */
export function getConfigBoolean(key: string, fallback: boolean = false): boolean {
  const value = getConfigValue(key);
  if (value === '' || value === undefined || value === null) return fallback;
  return ['true', '1'].includes(String(value).toLowerCase());
}

/**
 * Returns the secrets object from runtime config (for API keys, etc.).
 * Respects build-time process.env first (optional), then runtime config.
 * Never log or expose these values. Returns empty object if not present.
 */
export function getSecrets(): Record<string, string> {
  const config = getRuntimeConfig() ?? {};
  const runtimeSecrets = config['secrets'];
  return runtimeSecrets && typeof runtimeSecrets === 'object'
    ? (runtimeSecrets as Record<string, string>)
    : {};
}

/**
 * Gets a single secret by key.
 * Follows the same priority as getConfigValue: process.env > runtime config > fallback.
 */
export function getSecret(key: string, fallback: string = ''): string {
  // 1️⃣ Build-time override (optional, only for frontend-safe keys in .env)
  if (typeof process !== 'undefined') {
    const fromEnv = process.env?.[key];
    if (fromEnv !== undefined) {
      return fromEnv;
    }
  }

  // 2️⃣ Runtime secrets from config.json
  const secrets = getSecrets();
  const value = secrets[key];

  // 3️⃣ Fallback
  return value !== undefined && value !== '' ? value : fallback;
}